import {
  ActivatedOfferItem,
  OfferSource,
  OfferStatusInput,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  RoutePath,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useFetchMyActivatedOffers from 'features/rewards/hooks/useFetchMyActivatedOffers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import PillGroup from 'shared/components/vxl/navigation/PillGroup/PillGroup';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { isNotNilOrEmpty } from 'shared/utils/isNotNilOrEmpty/isNotNilOrEmpty';
import { useGetLocationCoordinates } from 'shared/utils/location/getLocationCoordinates';
import {
  showZendeskArticle,
  ZendeskArticles,
} from 'shared/utils/zendesk/zendeskUtils';

import OffersContentRenderer from './components/OffersContentRenderer/OffersContentRenderer';
import MyOffersProvider from './providers/MyOffersProvider';

const ACTIVE_TAB_ID = 'active';

interface MyOffersPillProps {
  id: string;
  labelText: string;
}

const MyOffers = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'myOffers',
  });

  const myOffersPillsData: MyOffersPillProps[] = [
    { id: ACTIVE_TAB_ID, labelText: translatedText('active') },
    { id: 'pending', labelText: translatedText('pending') },
  ];

  const [selectedTab, setSelectedTab] = useState<MyOffersPillProps>(
    myOffersPillsData[0],
  );

  const handleSupportPressAnalytics = usePressableAnalytics({
    analyticsId: 'cashbackFaqsClicked',
  });

  const handleBackArrowNavigation = (): void => {
    router.back();
  };

  const coordinates = useLocalSearchParams<{
    lat?: string;
    lng?: string;
  }>();

  const paramLat = coordinates.lat;
  const paramLng = coordinates.lng;

  const { access, hasLocationFetched, getLocationCoordinates } =
    useGetLocationCoordinates();

  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(
    paramLat && paramLng
      ? { latitude: parseFloat(paramLat), longitude: parseFloat(paramLng) }
      : null,
  );

  const status = useMemo(
    () =>
      selectedTab.id === ACTIVE_TAB_ID
        ? [OfferStatusInput.Activated, OfferStatusInput.Processing]
        : [OfferStatusInput.CashbackPending],
    [selectedTab.id],
  );

  useEffect(() => {
    const handleLocationAccess = async (): Promise<void> => {
      if (
        (paramLat && paramLng) ||
        access === 'checking' ||
        hasLocationFetched.current
      ) {
        return;
      }

      hasLocationFetched.current = true;
      const { coordinates: coords } = await getLocationCoordinates({
        shouldRequestPermission: true,
      });

      if (coords) {
        setLocation({
          latitude: coords.lat,
          longitude: coords.lng,
        });
      }
    };

    void handleLocationAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access]);

  const {
    activatedItems,
    inlineAlertBanner,
    loading,
    isPageLoading,
    error,
    refetch,
    pagination,
  } = useFetchMyActivatedOffers(location, status);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  const upsideItem = activatedItems?.find((item) => {
    const offerSource = item.offerSource ?? undefined;
    return offerSource === OfferSource.Upside || !offerSource;
  });
  const upsideData = upsideItem
    ? {
        offers: upsideItem.offers ?? [],
        title: upsideItem.title ?? '',
        subtitle: translatedText('footerMessage'),
      }
    : { offers: [], title: '', subtitle: '' };

  const selfFundedItems =
    activatedItems?.filter((item) => {
      const offerSource = 'offerSource' in item ? item.offerSource : undefined;
      return offerSource === OfferSource.SelfFunded;
    }) ?? [];

  const selfFundedSectionTitle =
    selfFundedItems.length > 0 ? selfFundedItems[0]?.title : undefined;

  const navigateToUpsideOffer = (
    offer: ActivatedOfferItem,
    coords?: { latitude: number; longitude: number } | null,
  ) => {
    if (!isNotNilOrEmpty(offer.id) || !isNotNilOrEmpty(offer.merchantId)) {
      return;
    }
    const params: {
      offerid: string;
      merchantId: string;
      lat?: string;
      lng?: string;
    } = { offerid: offer.id, merchantId: offer.merchantId };

    if (coords) {
      params.lat = coords.latitude.toString();
      params.lng = coords.longitude.toString();
    }
    router.push({ pathname: '/rewards/upside-offers/[merchantId]', params });
  };

  const handleOfferClick = (offer: ActivatedOfferItem) => {
    if (offer.offerSource === OfferSource.Upside) {
      navigateToUpsideOffer(offer, location);
    } else if (offer.onClick?.route) {
      router.push(offer.onClick.route as RoutePath);
    }
  };

  const isOverlayLoading = access === 'checking' || loading || !location;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              trailingAccessories={
                <IconButton
                  iconKey="QUESTION_MARK"
                  iconSize="MD"
                  iconButtonAccessibilityLabel="QUESTION_MARK"
                  iconOnPress={() => {
                    handleSupportPressAnalytics();
                    showZendeskArticle(ZendeskArticles?.CASHBACK);
                  }}
                  testID="question-mark-icon"
                />
              }
              primaryOnPress={handleBackArrowNavigation}
            />
          ),
        }}
      />

      <PillGroup
        initialSelectedPillId={selectedTab.id}
        pillConfigs={myOffersPillsData}
        onSelection={(tab: MyOffersPillProps) => setSelectedTab(tab)}
      />
      <MyOffersProvider
        selfFundedItems={selfFundedItems}
        selfFundedSectionTitle={selfFundedSectionTitle}
        upsideOffers={upsideData.offers}
        upsideSectionTitle={upsideData.title}
        upsideSectionSubtitle={upsideData.subtitle}
        inlineAlertBanner={inlineAlertBanner}
        isOverlayLoading={isOverlayLoading}
        hasOverlayError={!!error}
        isPageLoading={isPageLoading}
        onOfferClick={handleOfferClick}
      >
        <OffersContentRenderer
          selectedTabId={selectedTab.id}
          hasNextPage={pagination.hasNextPage}
          loadMore={pagination.loadMore}
        />
      </MyOffersProvider>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default MyOffers;
